/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';

/** Matches RequestAnalysisByUserController::OTHER_USERS - everyone outside the agency team */
export const OTHER_USERS = 'otherUsers';

export class RequestAnalysisByUser extends AbstractObject {
    /** The user on the full report, the OMC team once drilled down to one user */
    breakdownId: string;
    userId: uuid | typeof OTHER_USERS;
    omcTeamId: uuid;

    userName: string;
    email: string;
    fullName: string;
    contactType: string;
    omcName: string;

    backlogRequests = 0;
    backlogActions = 0;
    backlogTime = 0;
    requestsClosed = 0;
    actionsClosed = 0;
    timeSpent = 0;
    prevRequestsClosed = 0;
    prevActionsClosed = 0;
    prevTimeSpent = 0;

    /** Not from the server - stamped from the request so each row's links know their period */
    periodType: string;
    period: string;
}

export interface DailyTimeEntry {
    omcTeamId: uuid;
    omcName: string;
    /** 0 is Monday, matching MySQL WEEKDAY() */
    weekday: number;
    minutes: number;
}

/** Audit entries the user made on one model in the period - the parent itself when model is object */
export interface ModelActivity {
    object: string;
    model: string;
    created: number;
    updated: number;
    deleted: number;
}

/** Pass to HttpClient as { ...params } - an interface has no index signature, so is not accepted as params itself */
interface UserPeriodParams {
    periodType: string;
    period: string;
    userId: uuid;
    omcTeamId?: uuid;
}

@Injectable({
    providedIn: 'root'
})
export class RequestAnalysisByUserService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);

    protected get baseUrl() {
        return this.getAjaxPath('crm/requestAnalysisByUser');
    }
    protected cache: RequestAnalysisByUser[];
    protected typeString = 'RequestAnalysisByUser';

    getDailyTime(params: UserPeriodParams): Observable<DailyTimeEntry[]> {
        return this.http.get<DailyTimeEntry[]>(this.baseUrl + '/dailyTime', { params: { ...params } })
            .pipe(catchError(this.handleOneError<DailyTimeEntry[]>('getDailyTime', [])));
    }

    getActivity(params: UserPeriodParams): Observable<ModelActivity[]> {
        return this.http.get<ModelActivity[]>(this.baseUrl + '/activity', { params: { ...params } })
            .pipe(catchError(this.handleOneError<ModelActivity[]>('getActivity', [])));
    }
}
